import { Button, Chip, ChipGroup, Field, HStack, Pressable, Text, VStack, cx } from '@aj/ui';
import type { EntryKind, EntryView } from '@aj/core';
import { useEffect, useRef, useState } from 'react';

import { useActions, useRecentTracks } from '../data/hooks.js';
import { useAppData } from '../data/AppDataProvider.js';
import { useI18n, useT } from '../providers/I18nProvider.js';
import { ReadingForm } from './ReadingForm.js';

/**
 * Recording work done, in three taps.
 *
 * Self-monitoring is not a report *on* the intervention, it is the
 * intervention — so every field between "I did it" and "it is recorded"
 * attenuates the thing the app exists to do. Hence chips rather than a
 * dropdown, presets rather than a number pad, and no date picker at all: the
 * overwhelmingly common case is today, and backfill is a rule the command
 * layer already owns.
 *
 * What is deliberately NOT asked: `kind`. Learn-vs-review is the field that
 * powers the review scheduler, and asking for it here would cost a tap on
 * every single log to serve a distinction the app can infer — a review is
 * logged from a review card, and everything else is first contact.
 */

export type LogFormProps = {
  /** Called after the entry is written, so a sheet can close itself. */
  onDone: () => void;
  /**
   * An existing entry to correct rather than a new one to create.
   *
   * The same form, because the fields are the same fields — a second copy
   * would drift out of sync with this one on the first change to either.
   * Editing differs in exactly three ways: it opens pre-filled, it offers
   * `kind`, and it offers delete.
   */
  entry?: EntryView;
  /**
   * Pre-set by the review card, and by nothing else.
   *
   * This is what the log form's silence about `kind` depends on: a review is
   * logged FROM a review card, so the app knows without asking, and ordinary
   * logging never has to pose a question most students would answer wrong.
   */
  kind?: EntryKind;
  /** Pre-fill, when the caller already knows what this is. */
  track?: string;
  intention?: string;
};

export function LogForm({
  onDone,
  entry,
  kind: fixedKind,
  track: presetTrack,
  intention,
}: LogFormProps) {
  const t = useT();
  const i18n = useI18n();
  const { features } = useAppData();
  const actions = useActions();
  const tracks = useRecentTracks();

  const editing = entry !== undefined;
  const preset = entry ? features.log.minutePresets.includes(entry.minutes) : false;

  const [subject, setSubject] = useState(entry?.track ?? presetTrack ?? tracks[0] ?? '');
  const [typing, setTyping] = useState(!editing && presetTrack === undefined && tracks.length === 0);
  const [minutes, setMinutes] = useState<number | null>(preset ? (entry?.minutes ?? null) : null);
  const [kind, setKind] = useState<EntryKind>(entry?.kind ?? fixedKind ?? 'learn');
  // An edited entry whose duration is not one of the presets opens in the
  // custom field, already filled — otherwise correcting 37 minutes to 38 would
  // mean retyping it from nothing.
  const [custom, setCustom] = useState(editing && !preset ? String(entry?.minutes ?? '') : '');
  const [customOpen, setCustomOpen] = useState(editing && !preset);
  const [attaching, setAttaching] = useState(false);
  const subjectField = useRef<HTMLInputElement>(null);
  const customField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (typing) subjectField.current?.focus();
  }, [typing]);
  useEffect(() => {
    if (customOpen) customField.current?.focus();
  }, [customOpen]);

  const chosen = customOpen ? Number(custom) : minutes;
  const ready = subject.trim().length > 0 && Number.isFinite(chosen) && (chosen ?? 0) > 0;

  const submit = () => {
    if (!ready) return;
    if (entry) {
      void actions.correctEntry(entry.id, chosen as number, { track: subject.trim(), kind });
    } else {
      void actions.logEntry({
        track: subject.trim(),
        minutes: chosen as number,
        ...(fixedKind === undefined ? {} : { kind: fixedKind }),
        ...(intention === undefined ? {} : { intention }),
      });
    }
    onDone();
  };

  const remove = () => {
    if (!entry) return;
    void actions.deleteEntry(entry.id);
    onDone();
  };

  return (
    <VStack gap={4}>
      <VStack gap={2}>
        <Text variant="overline" tone="subtle">
          {t('log.subject')}
        </Text>

        {typing ? (
          <HStack gap={2}>
            <Field
              inputRef={subjectField}
              value={subject}
              onChange={setSubject}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submit();
                // Escape backs out to the chips rather than closing the sheet,
                // so a mistyped subject costs one key, not the whole flow.
                if (event.key === 'Escape' && tracks.length > 0) {
                  setTyping(false);
                  setSubject(tracks[0] ?? '');
                }
              }}
              label={t('log.subjectName')}
              className="flex-1"
            />
          </HStack>
        ) : (
          <HStack gap={2} className="flex-wrap">
            <ChipGroup
              label={t('log.subject')}
              options={tracks.map((track) => ({ value: track, label: track }))}
              value={subject}
              onSelect={setSubject}
            />
            <Pressable
              aria-label={t('log.subjectName')}
              onClick={() => {
                setSubject('');
                setTyping(true);
              }}
              className="rounded-full"
            >
              <Chip tone="neutral" icon="plus">
                {t('log.newSubject')}
              </Chip>
            </Pressable>
          </HStack>
        )}
      </VStack>

      <VStack gap={2}>
        <Text variant="overline" tone="subtle">
          {t('log.howLong')}
        </Text>

        <HStack gap={2} className="flex-wrap">
          {features.log.minutePresets.map((preset) => {
            const active = !customOpen && minutes === preset;
            return (
              <Pressable
                key={preset}
                aria-pressed={active}
                onClick={() => {
                  setMinutes(preset);
                  setCustomOpen(false);
                }}
                className={cx(
                  'min-h-11 rounded-lg border px-3 tabular-nums',
                  active
                    ? 'border-transparent bg-accent text-on-accent'
                    : 'border-line bg-sunken text-ink hover:bg-raised',
                )}
              >
                <Text variant="body" numeric className="text-current">
                  {i18n.formatDuration(preset)}
                </Text>
              </Pressable>
            );
          })}

          {customOpen ? (
            <Field
              numeric
              inputRef={customField}
              value={custom}
              onChange={(value) => setCustom(value.replace(/\D/g, ''))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submit();
                if (event.key === 'Escape') setCustomOpen(false);
              }}
              label={t('log.minutesName')}
              className="w-24 flex-none"
            />
          ) : (
            <Pressable
              aria-pressed={false}
              onClick={() => {
                setCustom('');
                setCustomOpen(true);
              }}
              className="min-h-11 rounded-lg border border-line bg-sunken px-3 text-ink hover:bg-raised"
            >
              <Text variant="body" className="text-current">
                {t('log.otherMinutes')}
              </Text>
            </Pressable>
          )}
        </HStack>
      </VStack>

      {/* Only when correcting. Logging never asks — the scheduler infers it,
          and this is the one place a wrong inference can be put right. */}
      {editing && (
        <VStack gap={2}>
          <Text variant="overline" tone="subtle">
            {t('log.kind')}
          </Text>
          <ChipGroup
            label={t('log.kind')}
            options={[
              { value: 'learn', label: t('log.kindLearn') },
              { value: 'review', label: t('log.kindReview') },
            ]}
            value={kind}
            onSelect={(value) => setKind(value as EntryKind)}
          />
        </VStack>
      )}

      {/* A student finishing a mock test has both facts at once: the minutes
          and the mark. Capturing them separately splits effort from outcome,
          which is the one link the whole product exists to draw. Offered only
          when creating — a correction is about fixing what is already here. */}
      {!editing && (
        <VStack gap={2}>
          {attaching ? (
            <ReadingForm
              onDone={() => setAttaching(false)}
              {...(subject.trim() ? { track: subject.trim() } : {})}
            />
          ) : (
            <Pressable
              onClick={() => setAttaching(true)}
              className="self-start rounded-full border border-line px-2.5 py-1 text-left text-xs text-subtle hover:border-accent hover:text-accent"
            >
              {t('measure.attach')}
            </Pressable>
          )}
        </VStack>
      )}

      <HStack gap={2} className="pt-1">
        <Button icon="check" fullWidth disabled={!ready} onClick={submit}>
          {t(editing ? 'log.save' : 'log.submit')}
        </Button>
        {editing && (
          <Button variant="secondary" icon="trash" aria-label={t('log.delete')} onClick={remove}>
            {t('log.delete')}
          </Button>
        )}
      </HStack>
    </VStack>
  );
}
